/**
 * Note: This form is using card from Stripe Elements https://stripe.com/docs/stripe-js#elements
 * Card is not a Final Form field so it's not available trough Final Form.
 * It's also handled separately in handleSubmit function.
 */
import React, { Component } from 'react';
import { bool, func, object, string } from 'prop-types';
import { Form as FinalForm } from 'react-final-form';
import classNames from 'classnames';
import config from '../../config';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { propTypes } from '../../util/types';
import { formatMoney } from '../../util/currency';
import { ensurePaymentMethodCard } from '../../util/data';
import { useConfirmationModal } from '../../util/hooks';
import { ensureStripeLoaded } from '../../util/stripeLoader';

import {
  Form,
  PrimaryButton,
  FieldCheckbox,
  FieldTextInput,
  IconSpinner,
  SavedCardDetails,
  StripePaymentAddress,
  SecondaryButton,
} from '../../components';
import css from './StripePaymentForm.module.css';

/**
 * Translate a Stripe API error object.
 *
 * To keep up with possible keys from the Stripe API, see:
 *
 * https://stripe.com/docs/api#errors
 *
 * Note that at least at moment, the above link doesn't list all the
 * error codes that the API returns.
 *
 * @param {Object} intl - react-intl object from injectIntl
 * @param {Object} stripeError - error object from Stripe API
 *
 * @return {String} translation message for the specific Stripe error,
 * or the given error message (not translated) if the specific error
 * type/code is not defined in the translations
 *
 */
const stripeErrorTranslation = (intl, stripeError) => {
  const { message, code, type } = stripeError;

  if (!code || !type) {
    // Not a proper Stripe error object
    return intl.formatMessage({ id: 'StripePaymentForm.genericError' });
  }

  const translationId =
    type === 'validation_error'
      ? `StripePaymentForm.stripe.validation_error.${code}`
      : `StripePaymentForm.stripe.${type}`;

  return intl.formatMessage({
    id: translationId,
    defaultMessage: message,
  });
};

const withoutEmpty = obj =>
  Object.fromEntries(Object.entries(obj).filter(([, v]) => v != null && v !== ''));

/**
 * The card field's text styles, read from the design tokens on its container.
 *
 * The card number, expiry and CVC are inputs inside Stripe's iframe, so our CSS can't reach them:
 * Stripe takes their styles as JS options (https://docs.stripe.com/js/appendix/style). The
 * container's CSS sets the same font size as the other fields (16px on phones, 14px from
 * viewportMedium), and this copies it, the font and the token colours into those options, so the
 * card field reads like FieldTextInput in both families.
 *
 * @param {CSSStyleDeclaration} computed getComputedStyle() of the card's container
 * @return {Object} a Stripe Style object
 */
export const cardElementStyle = computed => {
  const token = name => computed.getPropertyValue(name).trim();
  const muted = token('--colorTextMuted');
  return {
    base: withoutEmpty({
      color: token('--colorInk'),
      fontFamily: computed.fontFamily,
      fontSize: computed.fontSize,
      fontSmoothing: 'antialiased',
      iconColor: muted,
      '::placeholder': withoutEmpty({ color: muted }),
    }),
    invalid: withoutEmpty({
      color: token('--colorErrorText'),
      iconColor: token('--colorError'),
    }),
  };
};

const OneTimePaymentWithCardElement = props => {
  const { cardClasses, formId, handleStripeElementRef, hasCardError, error, label, intl } = props;
  const labelText =
    label || intl.formatMessage({ id: 'StripePaymentForm.saveAfterOnetimePayment' });
  return (
    <React.Fragment>
      <label className={css.paymentLabel} htmlFor={`${formId}-card`}>
        <FormattedMessage id="StripePaymentForm.paymentCardDetails" />
      </label>
      <div className={cardClasses} id={`${formId}-card`} ref={handleStripeElementRef} />
      {hasCardError ? <span className={css.error}>{error}</span> : null}
      <div className={css.saveForLaterUse}>
        <FieldCheckbox
          className={css.saveForLaterUseCheckbox}
          textClassName={css.saveForLaterUseLabel}
          id="saveAfterOnetimePayment"
          name="saveAfterOnetimePayment"
          label={labelText}
          value="saveAfterOnetimePayment"
        />
        <span className={css.saveForLaterUseLegalInfo}>
          <FormattedMessage id="StripePaymentForm.saveforLaterUseLegalInfo" />
        </span>
      </div>
    </React.Fragment>
  );
};

const PaymentMethodSelector = props => {
  const {
    cardClasses,
    formId,
    changePaymentMethod,
    defaultPaymentMethod,
    handleStripeElementRef,
    hasCardError,
    error,
    paymentMethod,
    intl,
  } = props;
  const last4Digits = defaultPaymentMethod.attributes.card.last4Digits;
  const labelText = intl.formatMessage(
    { id: 'StripePaymentForm.replaceAfterOnetimePayment' },
    { last4Digits }
  );

  return (
    <React.Fragment>
      <h3 className={css.paymentHeading}>
        <FormattedMessage id="StripePaymentForm.payWithHeading" />
      </h3>
      <SavedCardDetails
        className={css.paymentMethodSelector}
        card={defaultPaymentMethod.attributes.card}
        onChange={changePaymentMethod}
      />
      {paymentMethod === 'replaceCard' ? (
        <OneTimePaymentWithCardElement
          cardClasses={cardClasses}
          formId={formId}
          handleStripeElementRef={handleStripeElementRef}
          hasCardError={hasCardError}
          error={error}
          label={labelText}
          intl={intl}
        />
      ) : null}
    </React.Fragment>
  );
};

const getPaymentMethod = (selectedPaymentMethod, hasDefaultPaymentMethod) => {
  return selectedPaymentMethod == null && hasDefaultPaymentMethod
    ? 'defaultCard'
    : selectedPaymentMethod == null
    ? 'onetimeCardPayment'
    : selectedPaymentMethod;
};

// Should we show onetime payment fields and does StripeElements card need attention
const checkOnetimePaymentFields = (
  cardValueValid,
  selectedPaymentMethod,
  hasDefaultPaymentMethod,
  hasHandledCardPayment
) => {
  const useDefaultPaymentMethod =
    selectedPaymentMethod === 'defaultCard' && hasDefaultPaymentMethod;
  // Billing details are known if we have already handled card payment or existing default payment method is used.
  const billingDetailsKnown = hasHandledCardPayment || useDefaultPaymentMethod;

  // If onetime payment is used, check that the StripeElements card has valid value.
  const oneTimePaymentMethods = ['onetimeCardPayment', 'replaceCard'];
  const useOnetimePaymentMethod = oneTimePaymentMethods.includes(selectedPaymentMethod);
  const onetimePaymentNeedsAttention =
    !billingDetailsKnown && !(useOnetimePaymentMethod && cardValueValid);

  return {
    onetimePaymentNeedsAttention,
    showOnetimePaymentFields: useOnetimePaymentMethod,
  };
};

const initialState = {
  error: null,
  cardValueValid: false,
  cardFocused: false,
  // The mode can be 'onetimePayment', 'defaultCard', or 'replaceCard'
  // Check SavedCardDetails component for more information
  paymentMethod: null,
};

/**
 * Payment form that asks for credit card info using Stripe Elements.
 *
 * When the card is valid and the user submits the form, a request is
 * sent to the Stripe API to handle payment. `stripe.confirmCardPayment`
 * may ask more details from cardholder if 3D security steps are needed.
 *
 * See: https://stripe.com/docs/payments/payment-intents
 *      https://stripe.com/docs/elements
 */
class StripePaymentForm extends Component {
  constructor(props) {
    super(props);
    this.state = initialState;
    this.handleCardValueChange = this.handleCardValueChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.paymentForm = this.paymentForm.bind(this);
    this.initializeStripeElement = this.initializeStripeElement.bind(this);
    this.handleStripeElementRef = this.handleStripeElementRef.bind(this);
    this.changePaymentMethod = this.changePaymentMethod.bind(this);
    this.handleWindowResize = this.handleWindowResize.bind(this);
    this.handleCardFocus = this.handleCardFocus.bind(this);
    this.handleCardBlur = this.handleCardBlur.bind(this);
    this.finalFormAPI = null;
    this.cardContainer = null;
  }

  componentDidMount() {
    this._isMounted = true;
    if (!config.stripe.publishableKey) return;

    // Stripe.js is no longer on every page (see src/util/stripeLoader.js).
    // Checkout is the place that needs it, so it loads here, and the card
    // element is mounted once both the script and the container exist —
    // handleStripeElementRef covers the case where the ref arrives first.
    ensureStripeLoaded()
      .then(Stripe => {
        if (!this._isMounted) return;
        const {
          onStripeInitialized,
          hasHandledCardPayment,
          defaultPaymentMethod,
          loadingData,
        } = this.props;
        this.stripe = Stripe(config.stripe.publishableKey);
        onStripeInitialized(this.stripe);

        if (!(hasHandledCardPayment || defaultPaymentMethod || loadingData) && this.cardContainer) {
          this.initializeStripeElement();
        }
      })
      .catch(e => {
        console.error('[StripePaymentForm] Stripe.js failed to load:', e?.message || e);
      });
  }

  componentWillUnmount() {
    this._isMounted = false;
    this.unmountCard();
  }

  initializeStripeElement(element) {
    if (!this.card) {
      const container = element || this.cardContainer;
      const elements = this.stripe.elements();
      this.card = elements.create('card', {
        hidePostalCode: true,
        // Link's pop-up can't open under our Cross-Origin-Opener-Policy (same-origin), so Stripe
        // already hides its button on the deployed site; this hides it everywhere.
        disableLink: true,
        style: cardElementStyle(window.getComputedStyle(container)),
      });
      this.card.mount(container);
      this.card.addEventListener('change', this.handleCardValueChange);
      this.card.addEventListener('focus', this.handleCardFocus);
      this.card.addEventListener('blur', this.handleCardBlur);
      // Stripe can't see our media queries: follow the container's font size when it changes.
      this.cardFontSize = window.getComputedStyle(container).fontSize;
      window.addEventListener('resize', this.handleWindowResize);
    }
  }

  unmountCard() {
    if (this.card) {
      this.card.removeEventListener('change', this.handleCardValueChange);
      this.card.removeEventListener('focus', this.handleCardFocus);
      this.card.removeEventListener('blur', this.handleCardBlur);
      this.card.unmount();
      this.card = null;
    }
    window.removeEventListener('resize', this.handleWindowResize);
  }

  handleWindowResize() {
    if (!this.card || !this.cardContainer) return;
    const { fontSize } = window.getComputedStyle(this.cardContainer);
    if (fontSize && fontSize !== this.cardFontSize) {
      this.cardFontSize = fontSize;
      this.card.update({ style: { base: { fontSize } } });
    }
  }

  handleCardFocus() {
    this.setState({ cardFocused: true });
  }

  handleCardBlur() {
    this.setState({ cardFocused: false });
  }

  changePaymentMethod(changedTo) {
    const {
      currentUser,
      onCalculateCustomerTaxWithSavedBillingDetails,
      onResetTaxData,
    } = this.props;

    if (this.card && changedTo === 'defaultCard') {
      this.unmountCard();

      onCalculateCustomerTaxWithSavedBillingDetails(currentUser);
      this.setState({ cardValueValid: false, cardFocused: false });
    }

    onResetTaxData();
    this.setState({ paymentMethod: changedTo });
  }

  handleStripeElementRef(el) {
    this.cardContainer = el;
    if (this.stripe && el) {
      this.initializeStripeElement(el);
    }
  }

  handleCardValueChange(event) {
    const { intl } = this.props;
    const { error, complete } = event;

    this.setState(prevState => {
      return {
        error: error ? stripeErrorTranslation(intl, error) : null,
        cardValueValid: complete,
      };
    });
  }
  handleSubmit(values) {
    const {
      onSubmit,
      inProgress,
      formId,
      hasHandledCardPayment,
      defaultPaymentMethod,
    } = this.props;
    const { initialMessage } = values;
    const { cardValueValid, paymentMethod } = this.state;
    const hasDefaultPaymentMethod = defaultPaymentMethod?.id;
    const selectedPaymentMethod = getPaymentMethod(paymentMethod, hasDefaultPaymentMethod);
    const { onetimePaymentNeedsAttention } = checkOnetimePaymentFields(
      cardValueValid,
      selectedPaymentMethod,
      hasDefaultPaymentMethod,
      hasHandledCardPayment
    );

    if (inProgress || onetimePaymentNeedsAttention) {
      // Already submitting or card value incomplete/invalid
      return;
    }

    const params = {
      message: initialMessage ? initialMessage.trim() : null,
      card: this.card,
      formId,
      formValues: values,
      paymentMethod: getPaymentMethod(
        paymentMethod,
        ensurePaymentMethodCard(defaultPaymentMethod).id
      ),
    };
    onSubmit(params);
  }

  paymentForm(formRenderProps) {
    const { open: onOpenConfirmationModal, modalElement } = useConfirmationModal();

    const {
      className,
      rootClassName,
      inProgress: submitInProgress,
      loadingData,
      formId,
      paymentInfo,
      submitButtonMsg,
      authorDisplayName,
      showInitialMessageInput,
      intl,
      initiateOrderError,
      confirmCardPaymentError,
      confirmPaymentError,
      invalid,
      values,
      handleSubmit,
      form,
      hasHandledCardPayment,
      defaultPaymentMethod,
      onCalculateTax,
      calculateTaxInProgress,
      calculateTaxError,
      taxPrice,
      shouldCollectTax,
    } = formRenderProps;

    this.finalFormAPI = form;

    const ensuredDefaultPaymentMethod = ensurePaymentMethodCard(defaultPaymentMethod);
    const billingDetailsNeeded = !(hasHandledCardPayment || confirmPaymentError);

    const { cardValueValid, paymentMethod } = this.state;
    const hasDefaultPaymentMethod = ensuredDefaultPaymentMethod.id;
    const selectedPaymentMethod = getPaymentMethod(paymentMethod, hasDefaultPaymentMethod);
    const { onetimePaymentNeedsAttention, showOnetimePaymentFields } = checkOnetimePaymentFields(
      cardValueValid,
      selectedPaymentMethod,
      hasDefaultPaymentMethod,
      hasHandledCardPayment
    );

    const formattedTaxPrice = taxPrice ? formatMoney(intl, taxPrice) : null;

    const isDefaultCard = selectedPaymentMethod === 'defaultCard';
    const isTaxCalculationRequired = shouldCollectTax;
    const isPaymentInvalid = invalid || onetimePaymentNeedsAttention;
    const isSubmissionInProgress = submitInProgress || calculateTaxInProgress;
    const isTaxPriceMissing = !isDefaultCard && !taxPrice;
    const submitDisabled =
      isPaymentInvalid || isSubmissionInProgress || (isTaxCalculationRequired && isTaxPriceMissing);

    const hasCardError = this.state.error && !submitInProgress;
    const hasPaymentErrors = confirmCardPaymentError || confirmPaymentError;
    const classes = classNames(rootClassName || css.root, className);
    const cardClasses = classNames(css.card, {
      [css.cardFocus]: this.state.cardFocused,
      [css.cardSuccess]: this.state.cardValueValid,
      [css.cardError]: hasCardError,
    });

    // TODO: confirmCardPayment can create all kinds of errors.
    // Currently, we provide translation support for one:
    // https://stripe.com/docs/error-codes
    const piAuthenticationFailure = 'payment_intent_authentication_failure';
    const paymentErrorMessage =
      confirmCardPaymentError && confirmCardPaymentError.code === piAuthenticationFailure
        ? intl.formatMessage({ id: 'StripePaymentForm.confirmCardPaymentError' })
        : confirmCardPaymentError
        ? confirmCardPaymentError.message
        : confirmPaymentError
        ? intl.formatMessage({ id: 'StripePaymentForm.confirmPaymentError' })
        : intl.formatMessage({ id: 'StripePaymentForm.genericError' });

    const billingDetailsNameLabel = intl.formatMessage({
      id: 'StripePaymentForm.billingDetailsNameLabel',
    });

    const billingDetailsNamePlaceholder = intl.formatMessage({
      id: 'StripePaymentForm.billingDetailsNamePlaceholder',
    });

    const messagePlaceholder = intl.formatMessage(
      { id: 'StripePaymentForm.messagePlaceholder' },
      { name: authorDisplayName }
    );

    const messageOptionalText = intl.formatMessage({
      id: 'StripePaymentForm.messageOptionalText',
    });

    const initialMessageLabel = intl.formatMessage(
      { id: 'StripePaymentForm.messageLabel' },
      { messageOptionalText: messageOptionalText }
    );

    const getUserBillingDetailsSubmitValues = ({ name, ...billingDetails }) => billingDetails;
    const handleCalculateTax = async formValues => {
      const submitValues = getUserBillingDetailsSubmitValues(formValues);

      const confirm = await onOpenConfirmationModal({
        id: 'StripePaymentForm.calculateTaxConfirmModal',
        title: intl.formatMessage({ id: 'StripePaymentForm.calculateTaxConfirmTitle' }),
        description: intl.formatMessage({ id: 'StripePaymentForm.calculateTaxConfirmDescription' }),
      });

      if (confirm) {
        // Call the API to calculate tax
        onCalculateTax(submitValues);
      }
    };

    const isBillingDetailsIncomplete = (billingDetails = {}) => {
      const requiredFields = ['name', 'addressLine1', 'postal', 'city', 'country'];
      return requiredFields.some(field => !billingDetails[field]?.trim());
    };

    const calculateCustomerTaxInProgress = calculateTaxInProgress;
    const calculateCustomerTaxDisabled =
      calculateTaxInProgress || isBillingDetailsIncomplete(values);

    const calculateTaxErrorMessage = calculateTaxError ? (
      <p className={classNames(css.errorMessage, css.taxErrorMessage)}>
        {calculateTaxError.message ||
          intl.formatMessage({
            id: 'StripePaymentForm.calculateTaxError',
          })}
      </p>
    ) : null;

    const calculateTaxContainer = shouldCollectTax ? (
      <div className={css.taxContainer}>
        <h3 className={css.taxHeading}>
          {taxPrice ? (
            <FormattedMessage
              id="StripePaymentForm.taxCalculatedHeading"
              values={{
                taxPrice: formattedTaxPrice,
              }}
            />
          ) : (
            <FormattedMessage id="StripePaymentForm.taxHeading" />
          )}
        </h3>
        <p className={css.taxDescription}>
          <FormattedMessage id="StripePaymentForm.taxDescription" />
        </p>

        <SecondaryButton
          type="button"
          className={css.calculateTaxButton}
          onClick={() => handleCalculateTax(values)}
          inProgress={calculateCustomerTaxInProgress}
          disabled={calculateCustomerTaxDisabled}
        >
          <FormattedMessage
            id={taxPrice ? 'StripePaymentForm.recalculateTax' : 'StripePaymentForm.calculateTax'}
          />
        </SecondaryButton>

        {calculateTaxErrorMessage}
      </div>
    ) : null;

    // Asking billing address is recommended in PaymentIntent flow.
    // In CheckoutPage, we send name and email as billing details, but address only if it exists.
    const billingAddress = (
      <StripePaymentAddress intl={intl} form={form} fieldId={formId} card={this.card}>
        {calculateTaxContainer}
      </StripePaymentAddress>
    );

    const hasStripeKey = config.stripe.publishableKey;
    return hasStripeKey ? (
      <div className={classes}>
        <Form className={css.form} onSubmit={handleSubmit}>
          {billingDetailsNeeded && !loadingData ? (
            <React.Fragment>
              {hasDefaultPaymentMethod ? (
                <PaymentMethodSelector
                  cardClasses={cardClasses}
                  formId={formId}
                  defaultPaymentMethod={ensuredDefaultPaymentMethod}
                  changePaymentMethod={this.changePaymentMethod}
                  handleStripeElementRef={this.handleStripeElementRef}
                  hasCardError={hasCardError}
                  error={this.state.error}
                  paymentMethod={selectedPaymentMethod}
                  intl={intl}
                />
              ) : (
                <React.Fragment>
                  <h3 className={css.paymentHeading}>
                    <FormattedMessage id="StripePaymentForm.paymentHeading" />
                  </h3>
                  <OneTimePaymentWithCardElement
                    cardClasses={cardClasses}
                    formId={formId}
                    handleStripeElementRef={this.handleStripeElementRef}
                    hasCardError={hasCardError}
                    error={this.state.error}
                    intl={intl}
                  />
                </React.Fragment>
              )}

              {showOnetimePaymentFields ? (
                <div className={css.paymentAddressField}>
                  <h3 className={css.billingHeading}>
                    <FormattedMessage id="StripePaymentForm.billingDetails" />
                  </h3>
                  <FieldTextInput
                    className={css.field}
                    type="text"
                    id="name"
                    name="name"
                    autoComplete="cc-name"
                    label={billingDetailsNameLabel}
                    placeholder={billingDetailsNamePlaceholder}
                  />
                  {billingAddress}
                </div>
              ) : null}
            </React.Fragment>
          ) : loadingData ? (
            <p className={css.spinner}>
              <IconSpinner />
            </p>
          ) : null}

          {initiateOrderError ? (
            <span className={css.errorMessage}>{initiateOrderError.message}</span>
          ) : null}
          {showInitialMessageInput ? (
            <div>
              <h3 className={css.messageHeading}>
                <FormattedMessage id="StripePaymentForm.messageHeading" />
              </h3>
              <FieldTextInput
                type="textarea"
                id={`${formId}-message`}
                name="initialMessage"
                label={initialMessageLabel}
                placeholder={messagePlaceholder}
                className={css.message}
              />
            </div>
          ) : null}
          <div className={css.submitContainer}>
            {hasPaymentErrors ? (
              <span className={css.errorMessage}>{paymentErrorMessage}</span>
            ) : null}
            <p className={css.paymentInfo}>{paymentInfo}</p>
            <PrimaryButton
              className={css.submitButton}
              type="submit"
              inProgress={submitInProgress}
              disabled={submitDisabled}
            >
              {submitButtonMsg ? (
                submitButtonMsg
              ) : billingDetailsNeeded ? (
                <FormattedMessage id="StripePaymentForm.submitPaymentInfo" />
              ) : (
                <FormattedMessage id="StripePaymentForm.submitConfirmPaymentInfo" />
              )}
            </PrimaryButton>
          </div>
        </Form>
        {modalElement}
      </div>
    ) : (
      <div className={css.missingStripeKey}>
        <FormattedMessage id="StripePaymentForm.missingStripeKey" />
      </div>
    );
  }

  render() {
    const { onSubmit, ...rest } = this.props;
    return <FinalForm onSubmit={this.handleSubmit} {...rest} render={this.paymentForm} />;
  }
}

StripePaymentForm.defaultProps = {
  className: null,
  rootClassName: null,
  inProgress: false,
  loadingData: false,
  showInitialMessageInput: true,
  hasHandledCardPayment: false,
  defaultPaymentMethod: null,
  initiateOrderError: null,
  confirmCardPaymentError: null,
  confirmPaymentError: null,
  onCalculateTax: null,
  calculateTaxInProgress: false,
  calculateTaxError: null,
  taxPrice: null,
  shouldCollectTax: false,
};

StripePaymentForm.propTypes = {
  className: string,
  rootClassName: string,
  inProgress: bool,
  loadingData: bool,
  initiateOrderError: object,
  confirmCardPaymentError: object,
  confirmPaymentError: object,
  formId: string.isRequired,
  intl: intlShape.isRequired,
  onSubmit: func.isRequired,
  paymentInfo: string.isRequired,
  submitButtonMsg: string,
  authorDisplayName: string.isRequired,
  showInitialMessageInput: bool,
  hasHandledCardPayment: bool,
  defaultPaymentMethod: propTypes.defaultPaymentMethod,
  onCalculateTax: func.isRequired,
  calculateTaxInProgress: bool.isRequired,
  calculateTaxError: propTypes.error,
  taxPrice: propTypes.money,
  shouldCollectTax: bool.isRequired,
};

export default injectIntl(StripePaymentForm);
